import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../lib/auth';
import { employeesAPI, cardsAPI } from '../lib/api';
import {
    Users,
    CreditCard,
    CheckCircle2,
    ArrowRight,
    Plus,
    ChevronRight,
    Sparkles,
} from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';

const SETUP_KEY_PREFIX = 'yulontap_setup_';

export default function SetupMode() {
    const { t } = useTranslation();
    const { user, isSuperAdmin, isClientAdmin, isLocationManager } = useAuth();
    const navigate = useNavigate();
    const [dismissed, setDismissed] = useState(false);
    const [hasEmployees, setHasEmployees] = useState(null);
    const [hasAssignedCards, setHasAssignedCards] = useState(null);
    const [loading, setLoading] = useState(true);

    const storageKey = user ? `${SETUP_KEY_PREFIX}${user.id}` : null;

    // Check if setup was already completed or dismissed
    const isSetupDone = useCallback(() => {
        if (!storageKey) return true;
        return localStorage.getItem(storageKey) === 'done';
    }, [storageKey]);

    useEffect(() => {
        if (!user || isSuperAdmin()) {
            setLoading(false);
            return;
        }
        if (isSetupDone()) {
            setDismissed(true);
            setLoading(false);
            return;
        }
        checkProgress();
    }, [user]);

    const checkProgress = async () => {
        setLoading(true);
        try {
            const [empRes, cardRes] = await Promise.all([
                employeesAPI.list(),
                cardsAPI.list(),
            ]);
            const employees = empRes.data || [];
            const cards = cardRes.data || [];

            setHasEmployees(employees.length > 0);
            setHasAssignedCards(cards.some(c => c.employee_id));

            // Auto-complete if both steps are done
            if (employees.length > 0 && cards.some(c => c.employee_id)) {
                markDone();
            }
        } catch {
            // If API fails, don't block the user
        } finally {
            setLoading(false);
        }
    };

    const markDone = () => {
        if (storageKey) localStorage.setItem(storageKey, 'done');
        setDismissed(true);
    };

    // Don't show for Super Admin or if loading/dismissed
    if (isSuperAdmin() || dismissed || loading || !user) return null;
    if (!isClientAdmin() && !isLocationManager()) return null;

    const currentStep = !hasEmployees ? 1 : 2;
    const totalSteps = 2;

    const steps = [
        {
            num: 1,
            key: 'employees',
            icon: Users,
            title: t('setup.step1Title', 'Add Team Members'),
            desc: t('setup.step1Desc', 'Add the employees who will use Tap Engine Cards to generate reviews.'),
            action: t('setup.step1Action', 'Add Your First Employee'),
            done: hasEmployees,
            route: '/employees',
        },
        {
            num: 2,
            key: 'cards',
            icon: CreditCard,
            title: t('setup.step2Title', 'Assign Cards'),
            desc: t('setup.step2Desc', 'Assign a Tap Engine Card to each employee so they can start collecting reviews.'),
            action: t('setup.step2Action', 'Go to Cards'),
            done: hasAssignedCards,
            route: '/cards',
        },
    ];

    const activeStep = steps.find(s => !s.done) || steps[steps.length - 1];

    return (
        <Card className="border-blue-200 dark:border-blue-800 bg-gradient-to-br from-blue-50/60 to-slate-50/60 dark:from-blue-950/30 dark:to-slate-900/30 mb-6" data-testid="setup-mode-banner">
            <CardContent className="p-5">
                {/* Header row */}
                <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-2">
                        <Sparkles size={18} className="text-blue-500" />
                        <span className="text-sm font-semibold text-blue-700 dark:text-blue-300">
                            {t('setup.heading', 'Getting Started')}
                        </span>
                    </div>
                    <span className="text-xs font-medium text-muted-foreground">
                        {t('setup.stepOf', 'Step {{current}} of {{total}}', { current: currentStep, total: totalSteps })}
                    </span>
                </div>

                {/* Progress bar */}
                <div className="flex gap-1.5 mb-5">
                    {steps.map((s) => (
                        <div
                            key={s.num}
                            className={`h-1.5 flex-1 rounded-full transition-colors ${
                                s.done ? 'bg-emerald-500' : s.num === currentStep ? 'bg-blue-500' : 'bg-slate-200 dark:bg-slate-700'
                            }`}
                        />
                    ))}
                </div>

                {/* Steps list */}
                <div className="space-y-3">
                    {steps.map((step) => {
                        const isCurrent = step.num === currentStep && !step.done;
                        const Icon = step.icon;
                        return (
                            <div
                                key={step.num}
                                className={`flex items-center gap-3 p-3 rounded-lg transition-all ${
                                    step.done
                                        ? 'bg-emerald-50 dark:bg-emerald-900/20'
                                        : isCurrent
                                        ? 'bg-white dark:bg-slate-800 shadow-sm border border-blue-200 dark:border-blue-800'
                                        : 'bg-slate-50 dark:bg-slate-800/40 opacity-60'
                                }`}
                                data-testid={`setup-step-${step.num}`}
                            >
                                {/* Step icon */}
                                <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
                                    step.done
                                        ? 'bg-emerald-100 dark:bg-emerald-800'
                                        : isCurrent
                                        ? 'bg-blue-100 dark:bg-blue-900'
                                        : 'bg-slate-100 dark:bg-slate-700'
                                }`}>
                                    {step.done ? (
                                        <CheckCircle2 size={18} className="text-emerald-600 dark:text-emerald-400" />
                                    ) : (
                                        <Icon size={18} className={isCurrent ? 'text-blue-600 dark:text-blue-400' : 'text-muted-foreground'} />
                                    )}
                                </div>

                                {/* Text */}
                                <div className="flex-1 min-w-0">
                                    <p className={`text-sm font-medium ${step.done ? 'text-emerald-700 dark:text-emerald-300 line-through' : 'text-foreground'}`}>
                                        {step.title}
                                    </p>
                                    {isCurrent && (
                                        <p className="text-xs text-muted-foreground mt-0.5">{step.desc}</p>
                                    )}
                                </div>

                                {/* Action */}
                                {isCurrent && (
                                    <Button
                                        size="sm"
                                        onClick={() => navigate(step.route)}
                                        className="shrink-0"
                                        data-testid={`setup-action-${step.num}`}
                                    >
                                        {step.action}
                                        <ArrowRight size={14} className="ml-1.5" />
                                    </Button>
                                )}
                                {step.done && (
                                    <span className="text-xs text-emerald-600 dark:text-emerald-400 font-medium shrink-0">
                                        {t('common.success', 'Done')}
                                    </span>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* Skip link */}
                <div className="mt-4 text-center">
                    <button
                        onClick={markDone}
                        className="text-xs text-muted-foreground hover:text-foreground underline-offset-2 hover:underline transition-colors"
                        data-testid="setup-skip-btn"
                    >
                        {t('setup.skip', 'Skip setup — I\'ll do this later')}
                    </button>
                </div>
            </CardContent>
        </Card>
    );
}
